import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { LOADING_CIRCLE, Status, BUTTON_LOADING_CIRCLE } from 'common/utils/constants';
import Modal from 'components/shared/Modal/Modal';
import ScrollReactTable from 'components/shared/ScrollReactTable/ScrollReactTable';
import { PerfectScrollbar } from 'components/shared/ReactPerfectScrollbar';
import SvgAF from 'components/shared/SvgAF/SvgAF';
import { IconLoader } from 'common/IconLoader';
import classnames from 'classnames';
import 'components/features/Commissioning/CommissionCreateSiteScreen/CommissionCreateSiteScreen.css';
import { getConfigParams, getCode, getBundle } from 'apis/BundleManager';
import CommissionErrorBox from 'components/features/Commissioning/CommissionErrorBox/CommissionErrorBox';
import { TWO_HOP_ALIGNMENT_SCREEN_INDEXES } from '../utils';
import { isValidInteger } from 'common/utils/helpers';

const REGEX_PATTERNS = {
  name: /^[a-z0-9A-Z_ \.\-]+$/
};

const TwoHopPodCreation = (props) => {
  // read from commissioned bundle on initial load if recommissioning
  const [savedParameters, setSavedParameter] = useState({});
  const [bundleRequestStatus, setBundleRequestStatus] = useState(Status.NONE);
  const [nameInput, setNameInput] = useState('');
  const [code, setCode] = useState('');
  const [codeRequestStatus, setCodeRequestStatus] = useState(Status.NONE);
  const [latitude, setLatitude] = useState(props.selectedDevices[0]?.location?.lat || '');
  const [latitudeError, setLatitudeError] = useState('');
  const [longitude, setLongitude] = useState(props.selectedDevices[0]?.location?.long || '');
  const [longitudeError, setLongitudeError] = useState('');
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showCommissioningError, setShowCommissioningError] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [parameters, setParameters] = useState([]);
  const [paramInputs, setParamInputs] = useState([]);
  // use for auto focus after input value changes and input loses focus
  const [editingRow, setEditingRow] = useState(-1);
  const [nameInputError, setNameInputError] = useState('');
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [paramsRequestStatus, setParamsRequestStatus] = useState(Status.NONE);
  const SCREEN_INDEX = 1;

  let createBundleStatusName = 'CreateBundle';
  let createBundleStatus = useSelector(
    (store) => store.statuses && store.statuses.bundles && store.statuses.bundles[createBundleStatusName]
  );
  let patchBundleStatusName = `PatchBundle-${props.recommBundleId}`;
  let patchBundleStatus = useSelector(
    (store) => props.recommBundleId && store.statuses.bundles?.[patchBundleStatusName]
  );
  let initiateCommissioningStatusName = `initiateCommissioning-${
    props.bundleId ? props.bundleId : props.recommBundleId
  }`;
  let initiateCommissioningStatus = useSelector(
    (store) => (props.bundleId || props.recommBundleId) && store.statuses[initiateCommissioningStatusName]
  );

  const selectedDonorUnit = props.selectedDevices[0];

  useEffect(() => {
    if (selectedDonorUnit?.bundle?.name && selectedDonorUnit?.bundle?.code) {
      setCode(selectedDonorUnit.bundle.code);
      setNameInput(selectedDonorUnit.bundle.name);
      if (isInitialLoad) setIsInitialLoad(false);
      if (selectedDonorUnit.bundle.bundleId) {
        setBundleRequestStatus(Status.PENDING);
        getBundle(selectedDonorUnit.bundle.bundleId)
          .then((rawBundleData) => {
            setSavedParameter(rawBundleData.configurationParameters);
            setBundleRequestStatus(Status.SUCCESS);
          })
          .catch((e) => {
            setBundleRequestStatus(Status.FAILURE);
          });

        setParamsRequestStatus(Status.PENDING);
        getConfigParams()
          .then((params) => {
            setParameters(params);
            setParamsRequestStatus(Status.SUCCESS);
            setParamInputs(Array.apply(null, Array(params.length)).map(() => {}));
          })
          .catch((e) => {
            setParamsRequestStatus(Status.FAILURE);
          });
      }

      return;
    }
    setCodeRequestStatus(Status.PENDING);
    getCode()
      .then((res) => {
        setCodeRequestStatus(Status.SUCCESS);
        setCode(res.podCode);
      })
      .catch((e) => {
        setCodeRequestStatus(Status.FAILURE);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDonorUnit]);

  useEffect(() => {
    if (bundleRequestStatus !== Status.SUCCESS || paramsRequestStatus !== Status.SUCCESS) {
      return;
    }

    setParameters((params) => {
      const loadedParams = params.map((param) => {
        if (!savedParameters[param.name] || savedParameters[param.name] === param.default) {
          return param;
        }
        return {
          ...param,
          current: savedParameters[param.name]
        };
      });
      return loadedParams;
    });
  }, [savedParameters, bundleRequestStatus, paramsRequestStatus]);

  useEffect(() => {
    if (props.commissioningErrorCode) {
      setShowCommissioningError(true);
    }
    if (showSettings && parameters.length === 0) {
      setParamsRequestStatus(Status.PENDING);
      getConfigParams()
        .then((params) => {
          setParameters(params);
          setParamsRequestStatus(Status.SUCCESS);
          setParamInputs(Array.apply(null, Array(params.length)).map(() => {}));
        })
        .catch((e) => {
          setParamsRequestStatus(Status.FAILURE);
        });
    }
  }, [showSettings, showCommissioningError, initiateCommissioningStatus, createBundleStatus, patchBundleStatus]);

  const onClose = () => {
    props.onClose();
  };

  /**
   * update input value and potential error messages
   * @param {string} targetString
   */
  const inputOnChange = (targetString) => {
    if (isInitialLoad) setIsInitialLoad(false);
    let errorMessage = '';
    if (targetString.trim() === '') {
      errorMessage = 'Site ID cannot be empty.';
    } else if (targetString && !REGEX_PATTERNS.name.test(targetString)) {
      errorMessage = 'Site ID cannot have special characters.';
    }
    setNameInput(targetString);
    setNameInputError(errorMessage);
  };

  const nextOnClick = () => {
    if (!hasLocation()) {
      setShowConfirmation(true);
      return;
    }
    props.onNext(
      props.podNumber === 1
        ? TWO_HOP_ALIGNMENT_SCREEN_INDEXES.POD_CREATION_ONE
        : TWO_HOP_ALIGNMENT_SCREEN_INDEXES.POD_CREATION_TWO,
      getCommissionData()
    );
  };

  const getCommissionData = () => {
    let commissionData = {
      name: nameInput,
      type: 'Pivot 5g',
      code,
      memberPosition: {},
      members: []
    };

    for (let i = 0; i < props.selectedDevices.length; i++) {
      let deviceId = props.selectedDevices[i].deviceId;
      if (!deviceId) break;
      if (i === 0) {
        commissionData.memberPosition['du'] = deviceId;
      } else if (i === 1) {
        commissionData.memberPosition['su1'] = deviceId;
      } else {
        commissionData.memberPosition['su2'] = deviceId;
      }
      commissionData.members.push(deviceId);
    }

    if (latitude !== '' && longitude && latitudeError === '' && longitudeError === '') {
      commissionData.repeaterLocation = { lat: parseFloat(latitude), long: parseFloat(longitude) };
    }

    let address = '';
    [street, city, state, zip].forEach((a) => {
      if (a.trim() !== '') {
        address = address + a + ', ';
      }
    });
    if (address.trim() !== '') {
      commissionData.address = address.substring(0, address.length - 2);
    }

    // API contract
    const configParams = {};
    parameters.forEach((p) => {
      if (typeof p.default === 'string') {
        configParams[p.name] = p.current ?? p.default;
      } else {
        configParams[p.name] = parseFloat(isValidInteger(p.current) ? p.current : p.default);
      }
    });

    if (Object.keys(configParams).length !== 0) {
      commissionData.configurationParameters = configParams;
    }

    return commissionData;
  };

  const hasLocation = () => {
    if (latitude !== '' && longitude !== '') return true;
    if (street !== '' && city !== '' && state !== '' && zip !== '') return true;

    return false;
  };

  const getColumns = () => [0, 1, 2, 3].map((colIndex) => getSingleColumn(colIndex));

  const COLUMNS_INDEXES = {
    0: 'ITEM',
    1: 'CURRENT VALUE',
    2: 'DEFAULT VALUE',
    3: 'NEW VALUE'
  };

  const getSingleColumn = (colIndex) => ({
    Header: (col) => {
      return (
        <div className={'CreateSiteTable_Header_OuterDiv'}>
          <span className="ConfigHistoryTable_Header_Label">{COLUMNS_INDEXES[colIndex]}</span>
          {colIndex !== 3 && (
            <div className="ConfigHistoryTableHeader_ResizerSliderIcon">
              <div className="ConfigHistoryTableHeader_ResizerSliderIcon_Bar"></div>
            </div>
          )}
        </div>
      );
    },
    id: `create_site_${colIndex}`,
    resizable: true,
    minWidth: 100,
    width: getColWidth(colIndex),
    accessor: (device) => device,
    Cell: (row) => {
      return <div className="ConfigHistoryTable_cellWrapper">{getTableData(colIndex, row.index, row.value)}</div>;
    },
    fixed: 'left'
  });

  const getColWidth = (index) => {
    switch (index) {
      case 0:
        return 220;
      case 1:
        return 105;
      case 2:
        return 105;
      case 3:
        return '100%';
      default:
        return 100;
    }
  };

  const getTableData = (colIndex, rowIndex, item) => {
    switch (colIndex) {
      case 0:
        return item.displayName;
      case 1:
        return item.current === undefined ? item.default : item.current;
      case 2:
        return item.default;
      case 3:
        return (
          <div className="CreateSite_Settings_NewValueContainer">
            <div className="CreateSite_Settings_NewValueInput">
              <input
                className={classnames({ disabledInput: item.name === 'beamPOL' })}
                type="number"
                value={paramInputs[rowIndex]}
                onChange={(e) => {
                  settingInputOnChange(e.target.value, rowIndex);
                }}
                autoFocus={rowIndex === editingRow}
                readOnly={item.name === 'beamPOL'}
              ></input>
            </div>
            <div className="CreateSite_Settings_NewValueUnit">{item.unit}</div>
          </div>
        );
      default:
        return null;
    }
  };

  const settingInputOnChange = (value, index) => {
    let parsed = parseInt(value);
    if (isNaN(parsed) && value !== '') return;
    setEditingRow(index);
    setParamInputs([...paramInputs.slice(0, index), parsed, ...paramInputs.slice(index + 1)]);
  };

  const settingsBackOnClick = () => {
    resetValues();
  };

  const resetParamDefaults = () => {
    const defaultValues = parameters.map((param) => param.default);
    setParamInputs(defaultValues);
  };

  const onApplyChanges = () => {
    let newParams = parameters.map((p, i) => {
      let paramCopy = { ...p };
      if (paramInputs[i] !== undefined && paramInputs[i].length !== 0) {
        paramCopy.current = paramInputs[i];
      }
      return paramCopy;
    });
    setParameters(newParams);
    resetValues();
  };

  const resetValues = () => {
    setEditingRow(-1);
    const emptyInputs = parameters.map((p) => '');
    setParamInputs(emptyInputs);
    setShowSettings(false);
  };

  const defaultsChanged = () => {
    if (!parameters || parameters.length === 0) return false;

    if (
      parameters.some(
        (param) => param.current !== undefined && param.default !== undefined && param.current !== param.default
      )
    ) {
      return true;
    }

    return false;
  };

  const getDefaultParamsOverlay = () => {
    if (paramsRequestStatus === Status.PENDING) {
      return <div className="CreateSite_LoadingCircle_Overlay">{LOADING_CIRCLE}</div>;
    }
    if (paramsRequestStatus === Status.FAILURE) {
      return <div className="RequestErrorContainer">Fail to retrieve parameters. Please try again later.</div>;
    }

    return null;
  };

  const getScreenOverlay = () => {
    if (codeRequestStatus === Status.PENDING) {
      return <div className="CreateSite_LoadingCircle_Overlay">{LOADING_CIRCLE}</div>;
    }
    if (codeRequestStatus === Status.FAILURE) {
      return <div className="RequestErrorContainer">Fail to retrieve parameters. Please try again later.</div>;
    }

    return null;
  };

  let commissioningErrorText = null;
  switch (props.commissioningErrorCode) {
    case '400':
      commissioningErrorText = 'The parameters passed in the request body are invalid.';
      break;
    case '403':
      commissioningErrorText = 'Device is offline so action cannot be performed.';
      break;
    case '404':
      commissioningErrorText = 'Could not find requested source.';
      break;
    case '405':
      commissioningErrorText = 'Operation is not supported by device.';
      break;
    case '409':
      commissioningErrorText =
        'There was an active commissioning instance on the Pivot. You may retry it by clicking the Retry button.';
      break;
    case '500':
      commissioningErrorText = 'There has been an internal error in the server while processing the search request.';
      break;
    default:
      break;
  }

  let createBundleErrorText = null;
  switch (props.createBundleErrorCode) {
    case '400':
      createBundleErrorText = 'Bad request.';
      break;
    case '401':
      createBundleErrorText = 'Unauthorized.';
      break;
    case '403':
      createBundleErrorText = 'Forbidden error.';
      break;
    case '404':
      createBundleErrorText = 'Failed to set power targets before creating Pivot.';
      break;
    case '405':
      createBundleErrorText = 'Failed to set power targets before creating bundle.';
      break;
    case '500':
      createBundleErrorText = 'There has been an internal error in the server while processing the search request.';
      break;
    default:
      createBundleErrorText = 'Something went wrong. Please try it again later.';
      break;
  }

  let patchBundleErrorText = null;
  switch (props.patchBundleErrorCode) {
    case '400':
      patchBundleErrorText = 'Bad request.';
      break;
    case '401':
      patchBundleErrorText = 'Unauthorized.';
      break;
    case '403':
      patchBundleErrorText = 'Forbidden error.';
      break;
    case '404':
      patchBundleErrorText = 'Device not found.';
      break;
    case '500':
      patchBundleErrorText = 'There has been an internal error in the server while processing the search request.';
      break;
    default:
      patchBundleErrorText = 'Something went wrong. Please try it again later.';
      break;
  }

  let commissioningErrorModal = (
    <CommissionErrorBox
      title={'Commissioning error'}
      message={commissioningErrorText}
      onClose={props.onClose}
      action={props.retryCommissioningOp}
      isFetching={initiateCommissioningStatus === Status.PENDING}
    />
  );

  let createBundleErrorModal = (
    <CommissionErrorBox title={'Create Pivot error'} message={createBundleErrorText} onClose={onClose} />
  );

  let patchBundleErrorModal = (
    <CommissionErrorBox title={'Update Pivot error'} message={patchBundleErrorText} onClose={onClose} />
  );

  let isFetching =
    createBundleStatus === Status.PENDING ||
    patchBundleStatus === Status.PENDING ||
    initiateCommissioningStatus === Status.PENDING;

  return (
    <Modal
      animateX={false}
      global
      close={props.onClose}
      noPadding
      height={'fit-content'}
      width={'fit-content'}
      displayFlex={true}
      overflow={true}
      className={'RemoteCommissioning_VisibleOverflow'}
      // height={738}
      // width={565}
    >
      <div className="Commission_CreateSite_GlobalContainer">
        {!showSettings && (
          <div className="Commission_CreateSiteContainer">
            {showConfirmation && (
              <Modal animateX={false} close={() => setShowConfirmation(false)} noPadding height={266} width={480}>
                <div className="Commission_CreateSite_ConfirmationContainer">
                  <div className={'Commission_CreateSite_SiteSubheader'}>Proceed without the location information?</div>
                  <div className="Commission_CreateSite_ConfirmationText">
                    Without its location information, the POD can't be seen on the map..
                  </div>
                  <div className="Commission_CreateSite_ConfirmationBottomContainer">
                    <button
                      className="Commission_CreateSite_Bottom_CancelButton"
                      disabled={isFetching}
                      onClick={() => setShowConfirmation(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className={classnames({
                        Commission_CreateSite_Bottom_CommissionButton: true
                        // Commission_CreateSite_Bottom_CommissionButton_Disabled: nameInputError !== '' || deviceIdsValid()
                      })}
                      onClick={() => {
                        // props.handleNextButton()
                        if (props.commissioningErrorCode === 403) {
                        }

                        if (!props.commissioningErrorCode) {
                          props.onNext(TWO_HOP_ALIGNMENT_SCREEN_INDEXES.POD_CREATION, getCommissionData());
                        }
                      }}
                    >
                      {isFetching ? BUTTON_LOADING_CIRCLE : 'Next'}
                    </button>
                  </div>
                </div>
              </Modal>
            )}
            {props.commissioningErrorCode && commissioningErrorModal}
            {createBundleStatus === Status.FAILURE && createBundleErrorModal}
            {patchBundleStatus === Status.FAILURE && patchBundleErrorModal}
            <div className={'Commission_CreateSite_Header'}>Create a POD</div>
            <PerfectScrollbar>
              <div className="Commission_CreateSite_ScrollContainer">
                <div className={'Commission_CreateSite_Container'}>
                  <div className={'Commission_CreateSite_SiteSubheader'}>Selected devices for the Pivot</div>
                  {props.selectedDevices.map((device, index) => {
                    if (Object.keys(device).length === 0) return null;
                    return (
                      <div className={'Commission_CreateSite_SingleDeviceContainer'} key={index}>
                        <div className="Commission_CreateSite_SingleDeviceIcon">
                          <SvgAF
                            img={index === 0 ? IconLoader.icon_donor : IconLoader.icon_service}
                            hover={index === 0 ? IconLoader.icon_donor : IconLoader.icon_service}
                          />
                        </div>
                        <div className="Commission_CreateSite_SingleDeviceTypeText">
                          {index === 0 ? 'Donor' : 'Service'}
                        </div>
                        <div className="Commission_CreateSite_SingleDeviceIdText">{device.deviceId}</div>
                      </div>
                    );
                  })}
                </div>
                <div className={'Commission_CreateSite_Container'}>
                  <div className={'Commission_CreateSite_SiteSubheader'}>POD code and name</div>

                  <div className={'Commission_CreateSitePODContainer'}>
                    <div className="Commission_CreateSitePOD_SingleContainer">
                      <div className={'Commission_CreateSitePOD_Title'}>POD code</div>
                      <div className="Commission_CreateSitePOD_PODCode">
                        {codeRequestStatus === Status.PENDING ? BUTTON_LOADING_CIRCLE : code}
                      </div>
                      {codeRequestStatus === Status.FAILURE && (
                        <div className="Commission_CreateSite_InputError">Fail to generate a unique code.</div>
                      )}
                    </div>
                    <div className="Commission_CreateSitePOD_SingleContainer_Name">
                      <div className={'Commission_CreateSitePOD_Title'}>Site ID (Required)</div>
                      <div
                        className={classnames({
                          Commission_CreateSitePOD_PODName: true,
                          Commission_Create_Site_InputWithError: !isInitialLoad && nameInputError
                        })}
                      >
                        <input onChange={(evt) => inputOnChange(evt.target.value)} value={nameInput} type="text" />
                      </div>
                      {!isInitialLoad && nameInputError && (
                        <div className="Commission_CreateSite_InputError">{nameInputError}</div>
                      )}
                    </div>
                  </div>
                </div>
                <div className={'Commission_CreateSite_Container'}>
                  <div className={'Commission_CreateSite_SiteSubheader'}>Location</div>
                  {!hasLocation() && (
                    <div className="Commission_CreateSite_WarningContainer">
                      <div className="Commission_CreateSite_WarningIcon">
                        <SvgAF img={IconLoader.icon_warning_round} hover={IconLoader.icon_warning_round} />
                      </div>
                      <div className="Commission_CreateSite_WarningTextContainer">
                        <div className="Commission_CreateSite_WarningText">
                          Please provide either location coordinates or address for the POD.
                        </div>
                        <div className="Commission_CreateSite_WarningText">
                          Without the information, the POD can't be mappable.
                        </div>
                      </div>
                    </div>
                  )}
                  <div className={'Commission_CreateSite_LocationContainer'}>
                    <div className="Commission_CreateSite_LocationSingleContainer">
                      <div className="Commission_CreateSite_LocationTitle">Latitude: </div>
                      <div
                        className={classnames({
                          Commission_CreateSite_LocationInput: true,
                          Commission_Create_Site_InputWithError: latitudeError
                        })}
                      >
                        <input
                          onChange={(evt) => {
                            setLatitude(evt.target.value);
                            if (parseFloat(evt.target.value) < -90 || parseFloat(evt.target.value) > 90) {
                              setLatitudeError('Please input a value between -90 to 90');
                            } else {
                              setLatitudeError('');
                            }
                          }}
                          value={latitude}
                          type="number"
                          placeholder="00.0000000"
                        />
                      </div>
                      {latitudeError && <div className="Commission_CreateSite_InputError">{latitudeError}</div>}
                    </div>
                    <div className="Commission_CreateSite_LocationSingleContainer2">
                      <div className="Commission_CreateSite_LocationTitle">Longitude: </div>
                      <div
                        className={classnames({
                          Commission_CreateSite_LocationInput: true,
                          Commission_Create_Site_InputWithError: longitudeError
                        })}
                      >
                        <input
                          onChange={(evt) => {
                            if (parseFloat(evt.target.value) < -180 || parseFloat(evt.target.value) > 80) {
                              setLongitudeError('Please input a value between -180 to 80');
                            } else {
                              setLongitudeError('');
                            }
                            setLongitude(evt.target.value);
                          }}
                          value={longitude}
                          type="number"
                          placeholder="000.00000000"
                        />
                      </div>
                      {longitudeError && <div className="Commission_CreateSite_InputError">{longitudeError}</div>}
                    </div>
                  </div>
                  {(latitude === '' || longitude === '') && (
                    <div className={'Commission_CreateSite_LocationAddressContainer'}>
                      <div className="Commission_CreateSite_LocationAddressHeader">
                        <span className="Commission_CreateSite_LocationAddressOr">OR</span>
                        <span className="Commission_CreateSite_LocationAddressText">Address</span>
                      </div>
                      <div className="Commission_CreateSite_LocationAddress_StreetContainer">
                        <div className="Commission_CreateSite_LocationTitle">Street</div>
                        <div className={'Commission_CreateSite_StreetInput'}>
                          <input
                            onChange={(evt) => setStreet(evt.target.value)}
                            value={street}
                            type="text"
                            placeholder="Street name"
                          />
                        </div>
                      </div>
                      <div className="Commission_CreateSite_LocationAddress_SecondLineContainer">
                        <div className="Commission_CreateSite_LocationAddress_City">
                          <div className="Commission_CreateSite_LocationTitle">City</div>
                          <div className={'Commission_CreateSite_CityInput'}>
                            <input
                              onChange={(evt) => setCity(evt.target.value)}
                              value={city}
                              type="text"
                              placeholder="Name of city"
                            />
                          </div>
                        </div>
                        <div className="Commission_CreateSite_LocationAddress_State">
                          <div className="Commission_CreateSite_LocationTitle">State</div>
                          <div className={'Commission_CreateSite_StateInput'}>
                            <input
                              onChange={(evt) => setState(evt.target.value)}
                              value={state}
                              type="text"
                              maxLength="2"
                              placeholder="WA"
                            />
                          </div>
                        </div>
                        <div className="Commission_CreateSite_LocationAddress_Zip">
                          <div className="Commission_CreateSite_LocationTitle">Zip</div>
                          <div className={'Commission_CreateSite_ZipInput'}>
                            <input
                              onChange={(evt) => setZip(evt.target.value)}
                              value={zip}
                              type="text"
                              placeholder="00000"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </PerfectScrollbar>

            <div className={'Commission_CreateSite_BottomContainer'}>
              <div className="Commission_CreateSite_AdvancedSettings_Container">
                <div className="Commission_CreateSite_AdvancedSettings_Text" onClick={() => setShowSettings(true)}>
                  Advanced settings
                </div>
                {defaultsChanged() && <div className="Commission_CreateSite_SettingsHeader_NewValues">New values</div>}
              </div>
              <button className="Commission_CreateSite_Bottom_CancelButton" onClick={onClose}>
                Cancel
              </button>
              <button
                type="submit"
                className={classnames({
                  Commission_CreateSite_Bottom_CommissionButton: true,
                  Button_Disabled: code === '' || longitudeError !== '' || latitudeError !== '' || nameInputError !== ''
                })}
                onClick={nextOnClick}
              >
                {isFetching ? BUTTON_LOADING_CIRCLE : 'Next'}
              </button>
            </div>
          </div>
        )}
        {showSettings && (
          <Modal
            animateX={false}
            close={() => setShowConfirmation(false)}
            hideCloseButton={true}
            height={'fit-content'}
            width={'fit-content'}
            displayFlex={true}
          >
            <div className="Commission_CreateSite_SettingsContainer">
              <div className="Commission_CreateSite_SettingsHeader">
                <div className="Commission_CreateSite_SettingsHeaderIcon" onClick={settingsBackOnClick}>
                  <SvgAF img={IconLoader.left_arrow_default} hover={IconLoader.left_arrow_hover} />
                </div>
                <div className="Commission_CreateSite_SettingsHeaderText">Advanced settings</div>
              </div>
              <div className="Commission_CreateSite_SettingsWarningContainer">
                <div className="Commission_CreateSite_SettingsWarningIcon">
                  <SvgAF img={IconLoader.icon_warning_round} hover={IconLoader.icon_warning_round} />
                </div>
                <div className="Commission_CreateSite_SettingsWarningText">
                  These changes may change the operation of the Pivot Repeater and can reduce performance. Only make
                  changes if you have been properly trained in Pivot Operation and understand the risks.
                </div>
              </div>
              <div className="Commission_CreateSite_SettingsTableContainer">
                {getDefaultParamsOverlay()}
                {paramsRequestStatus === Status.SUCCESS && (
                  <ScrollReactTable
                    key={1}
                    showPagination={false}
                    resizable={true}
                    sortable={false}
                    minRows={2}
                    noDataText={''}
                    fetching={false}
                    className={'-highlight configHistory_Table Global_Table'}
                    defaultPageSize={0}
                    pageSize={parameters.length}
                    getTdProps={(state, rowInfo, column, instance) => {
                      return {
                        onClick: (e, handleOriginal) => {
                          if (handleOriginal) {
                            handleOriginal(); /* Suggested by documentation, makes sure that default click functions still get called if necessary*/
                          }
                        }
                      };
                    }}
                    data={parameters}
                    columns={[...getColumns()]}
                    withFixedColumns={true}
                  />
                )}
              </div>
              <div className="Commission_CreateSite_SettingsBottomContainer">
                <button
                  className={classnames({
                    Commission_CreateSite_Bottom_CancelButton: true,
                    Clear_Button_Disabled: paramsRequestStatus !== Status.SUCCESS
                  })}
                  onClick={resetParamDefaults}
                >
                  Reset to default
                </button>
                <button
                  type="submit"
                  className={classnames({
                    Commission_CreateSite_Bottom_CommissionButton: true,
                    Button_Disabled: paramsRequestStatus !== Status.SUCCESS
                  })}
                  onClick={onApplyChanges}
                >
                  Apply changes
                </button>
              </div>
            </div>
          </Modal>
        )}
      </div>
    </Modal>
  );
};

export default TwoHopPodCreation;
